/** @jsx jsx */

import React, { useState, useEffect, useMemo } from "react";
import { Card, Col, Progress, Row, Typography } from "antd";
import { jsx } from "@emotion/core";

import Button from "../../../components/button";
import useLoanStatus from "../../../hooks/auth/useLoanStatus";
import useRedirect from "../../../helperHooks/useRedirect";
import useEligibilityDetails from "../../../hooks/eligibility/useEligibilityDetails";
import useEligibilityMetaData from "../../../hooks/auth/useEligibilityMetaData";
import { getEligibilityRoute } from "../../../hooks/eligibility/useEligibilityRouting";

import {
  eligibilityCardContents as CONSTANTS,
  getEligibilityFoldName,
  getStatusMessage,
  getButtonLabel
} from "./EligibilityCardConstants";
import { color } from "../../../theme/Theme";
import { eligibilityCardStyles } from "./EligibilityCardStyles";
import { formatDate, localNumber } from "../../../helper/Format";

const { Text } = Typography;

const EligibilityCard: React.FC = () => {
  const {
    eligibilityMetaData: {
      status,
      subStatus,
      eligibilityAmount,
      amountUtilized,
      expiryDate,
      loading
    }
  } = useEligibilityMetaData();
  const redirect = useRedirect();
  const {
    isCreditApproved,
    inEligibilityFolds,
    hideEligibilityCardButton,
    isApplicationClosed
  } = useLoanStatus();
  const {
    eligibilityState: { eligibilityDetails }
  } = useEligibilityDetails();

  const [utilizedPercent, setUtilizedPercent] = useState<number>(0);

  const enableChart = useMemo(() => isCreditApproved && !isApplicationClosed, [
    isCreditApproved,
    isApplicationClosed
  ]);

  useEffect(() => {
    if (
      eligibilityAmount === null ||
      amountUtilized === null ||
      eligibilityAmount === 0
    ) {
      return;
    }
    setUtilizedPercent((amountUtilized / eligibilityAmount) * 100);
  }, [amountUtilized, eligibilityAmount]);

  const handleButtonClick = () => {
    const [routeToRedirectTo] = getEligibilityRoute(status);
    redirect(routeToRedirectTo);
  };

  const getResidueAmount = () => {
    if (eligibilityAmount === null || eligibilityAmount === 0) {
      return 0;
    }
    return eligibilityAmount - (amountUtilized || 0);
  };

  // To limit percentage to maximum 2 decimal places.
  const formatPercentage = (percentage: number) =>
    Math.round(percentage * 100) / 100;

  const renderRightColumn = () => {
    if (enableChart) {
      return (
        <section className="d-flex flex-column">
          <Text className="total-loan-amount mb-4">
            {CONSTANTS.labels.totalLoanAmount(eligibilityAmount || 0)}
          </Text>
          <Text className="d-flex align-items-center amount-bullet-point mb-3">
            <span className="utilize-amount-dot mr-2" />
            {CONSTANTS.labels.utilizeAmount(amountUtilized)}
          </Text>
          <Text className="d-flex align-items-center amount-bullet-point mb-3">
            <span className="residue-amount-dot mr-2" />
            {CONSTANTS.labels.residueAmount(getResidueAmount())}
          </Text>
        </section>
      );
    }
    if (inEligibilityFolds) {
      return (
        <React.Fragment>
          <Text className="d-block mb-4 eligibility-text">
            {CONSTANTS.labels.eligibilityCardFoldMessage(
              getEligibilityFoldName(status)
            )}
          </Text>
          <Text className="eligibility-text">
            {CONSTANTS.labels.eligibilityCardDisabled}
          </Text>
        </React.Fragment>
      );
    }
    if (isApplicationClosed) {
      return (
        <Text className="d-block mb-4 eligibility-text">
          {CONSTANTS.labels.applicationClosed}
          <br />
          {eligibilityDetails?.reason}
        </Text>
      );
    }
    // For other statuses, before credit approved, after eligibility folds.
    return (
      <Text className="d-block mb-4 eligibility-text">
        {getStatusMessage(
          status,
          eligibilityAmount || 0,
          subStatus.receivablesStatus
        )
          .split(".\n")
          .map((substring: string, i: number) => (
            <span key={i}>
              {substring}
              <br />
            </span>
          ))}
      </Text>
    );
  };

  return (
    <Card bordered={false} loading={loading} css={eligibilityCardStyles}>
      <Row type="flex" className="justify-content-between align-items-center">
        <Text className="home-card-title">{CONSTANTS.labels.eligibility}</Text>
        {!isCreditApproved && !hideEligibilityCardButton && (
          <Button type="primary" size="small" onClick={handleButtonClick}>
            {getButtonLabel(status, inEligibilityFolds)}
          </Button>
        )}
      </Row>
      <Row type="flex" className="my-5 align-items-center" gutter={[16, 0]}>
        <Col sm={24} lg={10} className="d-flex justify-content-center">
          <Progress
            type="circle"
            width={140}
            strokeWidth={10}
            percent={enableChart ? utilizedPercent : 0}
            trailColor={enableChart ? color.pink : color.placeholder}
            strokeColor={color.progressDarkBlue}
            format={percent =>
              enableChart ? (
                <section className="d-flex flex-column">
                  <span className="utilized-percent pb-2">
                    {formatPercentage(percent || 0)}%
                  </span>
                  <span className="percent-label pb-3">
                    {CONSTANTS.labels.chartUtilized}
                  </span>
                  <span className="residue-percent pb-2">
                    {percent ? formatPercentage(100 - (percent || 0)) : 100}%
                  </span>
                  <span className="percent-label">
                    {CONSTANTS.labels.chartResidue}
                  </span>
                </section>
              ) : (
                <span className={`${isCreditApproved ? "" : "disabled-text"}`}>
                  {percent}%
                </span>
              )
            }
          />
        </Col>
        <Col sm={24} lg={14} className="pt-3 pt-lg-0">
          {renderRightColumn()}
        </Col>
      </Row>
      {enableChart && (
        <Row type="flex" justify="space-between">
          <Text className="eligibility-card-footer">
            {CONSTANTS.labels.totalResidueAmount}
            <span className="eligibility-card-footer__bold">
              Rs {localNumber(getResidueAmount())}
            </span>
          </Text>
          {expiryDate && (
            <Text className="eligibility-card-footer">
              {CONSTANTS.labels.expiryDate}
              <span className="eligibility-card-footer__bold">
                {formatDate(expiryDate)}
              </span>
            </Text>
          )}
        </Row>
      )}
    </Card>
  );
};

export default EligibilityCard;
